import { z } from 'zod'

import { pageSchema } from '@/types/api/page'

import { apiClient } from './client'

export const DATE_FORMATS = ['dd/MM/yyyy', 'MM/dd/yyyy', 'yyyy-MM-dd'] as const

const workspaceSettingsSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  dateFormat: z.string(),
  ownerUserId: z.string().uuid(),
  pendingTransfer: z
    .object({ toUserId: z.string().uuid(), toEmail: z.string().nullable(), expiresAt: z.string() })
    .nullable(),
})

const auditEntrySchema = z.object({
  id: z.string().uuid(),
  action: z.string(),
  userId: z.string().uuid().nullable(),
  userName: z.string().nullable(),
  details: z.record(z.unknown()).nullable(),
  createdAt: z.string(),
})

export type WorkspaceSettings = z.infer<typeof workspaceSettingsSchema>
export type AuditEntry = z.infer<typeof auditEntrySchema>

export async function getWorkspaceSettings(): Promise<WorkspaceSettings> {
  const response = await apiClient.get('/workspaces/current')
  return workspaceSettingsSchema.parse(response.data)
}

export async function updateWorkspaceSettings(data: { name: string; dateFormat: string }): Promise<WorkspaceSettings> {
  const response = await apiClient.put('/workspaces/current', data)
  return workspaceSettingsSchema.parse(response.data)
}

export async function requestOwnershipTransfer(toUserId: string): Promise<void> {
  await apiClient.post('/workspaces/current/ownership-transfer', { toUserId })
}

export async function cancelOwnershipTransfer(): Promise<void> {
  await apiClient.delete('/workspaces/current/ownership-transfer')
}

export async function requestWorkspaceDeletion(): Promise<void> {
  await apiClient.post('/workspaces/current/deletion')
}

/** Completes an e-mailed owner action with the token from the link. */
export async function confirmWorkspaceAction(token: string): Promise<{ action: string; workspaceName: string }> {
  const response = await apiClient.post('/workspaces/confirm', { token })
  return z.object({ action: z.string(), workspaceName: z.string() }).parse(response.data)
}

export async function getAuditLogs(page: number, action?: string) {
  const response = await apiClient.get('/workspaces/current/audit-logs', { params: { page, size: 25, action } })
  return pageSchema(auditEntrySchema).parse(response.data)
}
